import { useCallback, useContext, useEffect, useState } from 'react';
import MobileFormContext from '../../../../components/mobileForm/context/mobileFormContext';
import { mobileFormFieldsTypes, textfieldTypes } from '../../../../config/constants/mobileFormConstant';

const typeIdToValue = Object.values(mobileFormFieldsTypes).reduce((acc, item) => {
  if (item.id != null) acc[item.id] = item.value;
  return acc;
}, {});

const textFieldIdToValue = Object.values(textfieldTypes).reduce((acc, item) => {
  acc[item.id] = item.name;
  return acc;
}, {});

const findTemplateFieldByIdInput = (fields, idInput) => {
  for (const field of fields ?? []) {
    if (field.idInput === idInput) return field;
    if (typeIdToValue[field.id] === mobileFormFieldsTypes.radios.value) {
      for (const item of field.list ?? []) {
        const found = findTemplateFieldByIdInput(item.list ?? [], idInput);
        if (found) return found;
      }
    } else if (Array.isArray(field.list)) {
      const found = findTemplateFieldByIdInput(field.list, idInput);
      if (found) return found;
    }
  }
  return null;
};

const convertTemplateFieldToSaved = (templateField) => {
  const type =
    templateField.id === mobileFormFieldsTypes.list.id
      ? templateField.multiple
        ? mobileFormFieldsTypes.listMultiple.value
        : mobileFormFieldsTypes.list.value
      : typeIdToValue[templateField.id];
  if (!type) return null;

  const base = {
    id: templateField.id,
    idInput: templateField.idInput,
    label: templateField.label,
    type,
    alert: null,
  };

  if (type === mobileFormFieldsTypes.radios.value) {
    return {
      ...base,
      value: (templateField.list ?? []).map((item) => ({
        idInput: item.idInput,
        label: item.label,
        value: false,
        alert: null,
        valueConditional: item.conditional ? [] : null,
      })),
    };
  }
  if (type === mobileFormFieldsTypes.checkboxGroup.value) {
    return {
      ...base,
      value: (templateField.list ?? []).map((item) => ({
        idInput: item.idInput,
        label: item.label,
        value: false,
        alert: null,
      })),
    };
  }
  if (type === mobileFormFieldsTypes.text.value || type === mobileFormFieldsTypes.textArea.value) {
    return {
      ...base,
      typeTextField: textFieldIdToValue[templateField.typeTextField?.id] ?? textfieldTypes.TEXT.name,
      value: null,
    };
  }
  if (type === mobileFormFieldsTypes.list.value || type === mobileFormFieldsTypes.listMultiple.value) {
    return { ...base, value: null, nodes: [] };
  }
  return { ...base, value: null };
};

export const useRadioConditionalFields = ({ field }) => {
  const { applicationForm } = useContext(MobileFormContext);

  const [selectedIdInput, setSelectedIdInput] = useState(
    () => field.value?.find((item) => item.value)?.idInput ?? null,
  );
  const [templateRadioItems, setTemplateRadioItems] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [conditionalFields, setConditionalFields] = useState([]);

  useEffect(() => {
    if (!applicationForm) return;
    let radioField = null;
    for (const page of applicationForm) {
      radioField = findTemplateFieldByIdInput(page.list ?? [], field.idInput);
      if (radioField) break;
    }
    setTemplateRadioItems(radioField?.list ?? []);
    setLoadingTemplate(false);
  }, [applicationForm, field.idInput]);

  const getConditionalFields = useCallback(
    (item) => {
      const savedConditionals = field.value?.find((v) => v.idInput === item.idInput)?.valueConditional ?? [];
      if (savedConditionals.length > 0) return savedConditionals;
      const templateItem = templateRadioItems.find((t) => t.idInput === item.idInput);
      return (templateItem?.list ?? []).map(convertTemplateFieldToSaved).filter(Boolean);
    },
    [field.value, templateRadioItems],
  );

  useEffect(() => {
    if (!selectedIdInput || templateRadioItems.length === 0) return;
    const templateItem = templateRadioItems.find((t) => t.idInput === selectedIdInput);
    if (!templateItem?.conditional) return;
    const computed = getConditionalFields({ idInput: selectedIdInput });
    setConditionalFields((prev) => (prev.length > 0 ? prev : computed));
  }, [selectedIdInput, templateRadioItems, getConditionalFields]);

  const handleRadioChange = (changeEvent) => {
    const newIdInput = changeEvent.target.value;
    setSelectedIdInput(newIdInput);
    setConditionalFields([]);
  };

  return {
    selectedIdInput,
    conditionalFields,
    templateRadioItems,
    loadingTemplate,
    getConditionalFields,
    handleRadioChange,
  };
};
